import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Camera, Save } from 'lucide-react';
import { toast } from 'sonner';

const EditProfilePage = () => {
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    phone: '',
    city: '',
    birth_year: '',
    playing_hand: '',
    years_playing: '',
    favorite_surface: '',
    bio: '',
    avatar: '',
    // Playing Style
    playing_style: '',
    strengths: '',
    weaknesses: '',
    // Favorite Players
    favorite_player_1: '',
    favorite_player_2: '',
    favorite_player_3: '',
    // Equipment
    racket: '',
    shoes: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    
    // Load current user data
    setProfileData({
      full_name: user.full_name || '',
      phone: user.phone || '',
      city: user.city || '',
      birth_year: user.birth_year || '',
      playing_hand: user.playing_hand || '',
      years_playing: user.years_playing || '',
      favorite_surface: user.favorite_surface || '',
      bio: user.bio || '',
      avatar: user.avatar || '',
      playing_style: user.playing_style || '',
      strengths: user.strengths || '',
      weaknesses: user.weaknesses || '',
      favorite_player_1: user.favorite_player_1 || '',
      favorite_player_2: user.favorite_player_2 || '',
      favorite_player_3: user.favorite_player_3 || '',
      racket: user.racket || '',
      shoes: user.shoes || ''
    });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Clean data - send only fields that have values
      const updateData = {};
      Object.keys(profileData).forEach(key => {
        if (profileData[key] !== '' && profileData[key] !== null) {
          updateData[key] = profileData[key];
        }
      });

      await axios.put(`${API}/users/profile`, updateData);
      toast.success('הפרופיל עודכן בהצלחה!');
      
      // Refresh page to get updated user data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'שגיאה בעדכון הפרופיל');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // In a real app, upload to server/cloud storage
      // For now, use a data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileData({...profileData, avatar: reader.result});
        toast.success('תמונה נטענה! לא לשכוח לשמור');
      };
      reader.readAsDataURL(file);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 py-8 md:py-12">
        <Card className="glass-effect" data-testid="edit-profile-card">
          <CardHeader>
            <CardTitle className="text-2xl md:text-3xl">עריכת פרופיל</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Avatar */}
              <div className="flex flex-col items-center gap-4">
                <Avatar className="w-32 h-32">
                  {profileData.avatar ? (
                    <img src={profileData.avatar} alt="Profile" className="w-full h-full object-cover" />
                  ) : (
                    <AvatarFallback className="text-4xl bg-gradient-to-r from-blue-600 to-green-600 text-white">
                      {user.full_name.charAt(0)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <input
                    type="file"
                    id="avatar-upload"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarUpload}
                  />
                  <Label htmlFor="avatar-upload">
                    <Button type="button" variant="outline" className="cursor-pointer" asChild>
                      <span>
                        <Camera className="w-4 h-4 ml-2" />
                        העלה תמונת פרופיל
                      </span>
                    </Button>
                  </Label>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">שם מלא</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={profileData.full_name}
                    onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                    data-testid="edit-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">טלפון</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                    data-testid="edit-phone-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">עיר</Label>
                  <Input
                    id="city"
                    type="text"
                    placeholder="תל אביב"
                    value={profileData.city}
                    onChange={(e) => setProfileData({...profileData, city: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birth_year">שנת לידה</Label>
                  <Input
                    id="birth_year"
                    type="number"
                    placeholder="1990"
                    min="1940"
                    max={new Date().getFullYear()}
                    value={profileData.birth_year}
                    onChange={(e) => setProfileData({...profileData, birth_year: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="playing_hand">יד משחק</Label>
                  <Select
                    value={profileData.playing_hand}
                    onValueChange={(value) => setProfileData({...profileData, playing_hand: value})}
                  >
                    <SelectTrigger id="playing_hand">
                      <SelectValue placeholder="בחר יד" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ימין">ימין</SelectItem>
                      <SelectItem value="שמאל">שמאל</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="years_playing">שנות משחק</Label>
                  <Input
                    id="years_playing"
                    type="number"
                    placeholder="5"
                    min="0"
                    max="80"
                    value={profileData.years_playing}
                    onChange={(e) => setProfileData({...profileData, years_playing: e.target.value})}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="favorite_surface">משטח אהוב</Label>
                <Select
                  value={profileData.favorite_surface}
                  onValueChange={(value) => setProfileData({...profileData, favorite_surface: value})}
                >
                  <SelectTrigger id="favorite_surface">
                    <SelectValue placeholder="בחר משטח" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="קשה">קשה (Hard Court)</SelectItem>
                    <SelectItem value="חימר">חימר (Clay)</SelectItem>
                    <SelectItem value="דשא">דשא (Grass)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bio">אודות</Label>
                <Textarea
                  id="bio"
                  placeholder="ספר/י קצת על עצמך, סגנון המשחק שלך, הישגים..."
                  rows={4}
                  value={profileData.bio}
                  onChange={(e) => setProfileData({...profileData, bio: e.target.value})}
                  maxLength={500}
                />
                <p className="text-xs text-gray-500">
                  {profileData.bio.length}/500 תווים
                </p>
              </div>

              {/* Playing Style Section */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span>🎾</span> סגנון משחק
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="playing_style">סגנון משחק</Label>
                    <Select
                      value={profileData.playing_style}
                      onValueChange={(value) => setProfileData({...profileData, playing_style: value})}
                    >
                      <SelectTrigger id="playing_style">
                        <SelectValue placeholder="בחר סגנון משחק" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Aggressive Baseliner">Aggressive Baseliner</SelectItem>
                        <SelectItem value="Defensive Baseliner">Defensive Baseliner</SelectItem>
                        <SelectItem value="Serve & Volley">Serve & Volley</SelectItem>
                        <SelectItem value="All-Court Player">All-Court Player</SelectItem>
                        <SelectItem value="Counter Puncher">Counter Puncher</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="strengths">נקודות חזקות</Label>
                    <Input
                      id="strengths"
                      type="text"
                      placeholder="מכה ימין, הגשה, ולי..."
                      value={profileData.strengths}
                      onChange={(e) => setProfileData({...profileData, strengths: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="weaknesses">נקודות לשיפור</Label>
                    <Input
                      id="weaknesses"
                      type="text"
                      placeholder="בקהנד, ולי, משחק רשת..."
                      value={profileData.weaknesses}
                      onChange={(e) => setProfileData({...profileData, weaknesses: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Favorite Players Section */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span>⭐</span> שחקנים מועדפים
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="favorite_player_1">שחקן מועדף #1</Label>
                    <Input
                      id="favorite_player_1"
                      type="text"
                      placeholder="Novak Djokovic"
                      value={profileData.favorite_player_1}
                      onChange={(e) => setProfileData({...profileData, favorite_player_1: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="favorite_player_2">שחקן מועדף #2</Label>
                    <Input
                      id="favorite_player_2"
                      type="text"
                      placeholder="Roger Federer"
                      value={profileData.favorite_player_2}
                      onChange={(e) => setProfileData({...profileData, favorite_player_2: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="favorite_player_3">שחקן מועדף #3</Label>
                    <Input
                      id="favorite_player_3"
                      type="text"
                      placeholder="Rafael Nadal"
                      value={profileData.favorite_player_3}
                      onChange={(e) => setProfileData({...profileData, favorite_player_3: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Equipment Section */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span>🛍️</span> הציוד שלי
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="racket">רקטה</Label>
                    <Input
                      id="racket"
                      type="text"
                      placeholder="Wilson Pro Staff 97"
                      value={profileData.racket}
                      onChange={(e) => setProfileData({...profileData, racket: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="shoes">נעליים</Label>
                    <Input
                      id="shoes"
                      type="text"
                      placeholder="Nike Court Vapor"
                      value={profileData.shoes}
                      onChange={(e) => setProfileData({...profileData, shoes: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="submit"
                  className="flex-1 btn-primary"
                  disabled={loading}
                  data-testid="save-profile-btn"
                >
                  <Save className="w-4 h-4 ml-2" />
                  {loading ? 'שומר...' : 'שמור שינויים'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/profile/${user.id}`)}
                  data-testid="cancel-edit-btn"
                >
                  ביטול
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EditProfilePage;
